// Canonical Zod schemas for the app data layer. Per spec/tech-arch.md §2.4
// (Template), §2.5 (Email), §5 (AppState) + spec/build-plan.md P-020 row.
//
// **Schema-as-source-of-truth.** Per Tech Arch §2 lead-in, all data
// structures are Zod schemas; TS types derive via `z.infer<typeof X>`.
// One source for shape, validation, type-checking. Forms (P-025+) read
// the per-Block schemas; persistence (P-021) parses against AppStateSchema
// at the localStorage boundary; JSON import (P-034) parses against
// EmailSchema / TemplateSchema.
//
// **`included: z.boolean().default(true)` parsed-vs-input asymmetry.**
// Zod's `.default(true)` accepts `{...}` (no included key) on input but
// guarantees `included: boolean` on output. `z.infer` returns the output
// type, so `EmailBlockInstance['included']` is non-optional `boolean` —
// matching the prior local interface in render-block.ts (P-018 / D-077).
// At construction sites, prefer omitting the key over `included: undefined`
// (D-006's exactOptionalPropertyTypes rejects the latter).
//
// **`options` and `inputs` typed `z.unknown()`.** Per Tech Arch §2.4 + §2.5:
// shape varies per blockId; per-Block narrowing happens at the render seam
// (render-block.ts's `parseOrThrow` against the looked-up BlockDefinition's
// schemas, D-077 + D-079). The wrapper schemas can't narrow without
// importing the registry, which would couple the data layer to the design-
// system layer.
//
// **`CURRENT_SCHEMA_VERSION = 1`** is the v1 init baseline. P-021 wires the
// migration registry (Tech Arch §5.2); P-020 establishes the constant only.

import { z } from 'zod'

/**
 * One Block instance inside a Template's `blocks` array. Lifts to a
 * top-level type via `z.infer<typeof TemplateSchema>['blocks'][number]`
 * (forward use at P-031 template editor).
 */
export const TemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  schemaVersion: z.number(),
  blocks: z.array(
    z.object({
      instanceId: z.string(),
      blockId: z.string(),
      options: z.unknown(),
      // Per-instance Template-time `inputs` defaults that pre-fill into the
      // Email's `inputs` at `createEmailFromTemplate` time (P-037.31 / D-139).
      // Shape varies per blockId (mirrors `options`); validation deferred to
      // the per-Block schema at the render seam. Absent on pre-v12 records.
      defaultInputs: z.unknown().optional(),
    }),
  ),
})

/**
 * One Block instance inside an Email's `blocks` array. Lifts to a
 * top-level type via `z.infer<typeof EmailSchema>['blocks'][number]` —
 * this is the canonical `EmailBlockInstance` consumed by render-block.ts
 * (P-018 / D-077) and render-email.ts (P-019 / D-081). The `included`
 * field has `.default(true)`: input shape `included?: boolean`, output
 * shape `included: boolean`.
 */
export const EmailSchema = z.object({
  id: z.string(),
  name: z.string(),
  templateId: z.string(),
  templateName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  schemaVersion: z.number(),
  // V2.2 (P-047 / D-142): CC Push API needs a subject + preheader on every
  // campaign. Both are persisted on the Email so staff can compose them
  // alongside the body and they round-trip across browser sessions.
  // `subjectLine` is `z.string()` (no `.min(1)`) so the field tolerates the
  // empty state — mirrors `name` (D-135). The Push UI gates on non-empty;
  // V2.1 + V1 paths don't consume the field at all. `previewText` is
  // `.optional()` because there is no use case for the empty-string sentinel
  // vs missing distinction.
  subjectLine: z.string().default(''),
  previewText: z.string().optional(),
  blocks: z.array(
    z.object({
      instanceId: z.string(),
      blockId: z.string(),
      options: z.unknown(),
      inputs: z.unknown(),
      included: z.boolean().default(true),
    }),
  ),
})

/**
 * Top-level app state. Parsed against on app boot (P-021 wires the
 * localStorage round-trip). `currentEmailId` / `currentTemplateId` are
 * UI selection state — null when nothing is open. `schemaVersion` gates
 * wrapper-shape migrations (per-Template / per-Email schemaVersion gate
 * the per-record shape).
 */
export const AppStateSchema = z.object({
  templates: z.array(TemplateSchema),
  emails: z.array(EmailSchema),
  currentEmailId: z.string().nullable(),
  currentTemplateId: z.string().nullable(),
  schemaVersion: z.number(),
})

/**
 * Wrapper-shape schema version. Bump when AppState's top-level shape
 * changes (e.g., adding a new top-level key). Per-Template / per-Email
 * `schemaVersion` fields gate per-record migrations independently.
 * P-021 wires the migration registry; P-020 establishes the constant.
 *
 * **v1 → v2 (P-037):** Header Block schema change. `header.options.style`
 * widened from 3 → 7 values; `header.inputs.subhead` dropped. The v1→v2
 * migration walks every Email + Template's `blocks` array and rewrites
 * any header block: old style → new style mapping
 * (newsletter → signal, events → big-3, general → announcement) +
 * inputs reset to `{}`.
 *
 * **v2 → v3 (P-037.16 D-116):** Block inventory + per-block schema
 * changes from Banks's batched audit:
 * - `member-shoutout` Block dropped (filtered out of every blocks[] array).
 * - `welcome-new-members` renamed → `member-list`; per-member `photoUrl`
 *   + `partnerLocation` dropped; `options.columns` dropped (fixed at 3
 *   in the new schema; the field is permissively-stripped on parse).
 *
 * **v3 → v4 (P-037.18 D-118):** Block inventory + per-block schema
 * changes from the next batched audit:
 * - `sponsors-needed-cta` Block dropped (filtered out of every blocks[]
 *   array — leftover instances would fail registry lookup at render time).
 * - `day-chairs.options.layout` field dropped (the new schema is
 *   `z.object({})` so Zod's default strip mode discards stray keys
 *   silently; no explicit migration step needed).
 * - `footer.options.theme` field dropped (same: new schema is
 *   `z.object({})`; Zod strips).
 *
 * **v5 (P-037.20 D-126).** `inputs.heading` field dropped from 5 Blocks
 * (`day-chairs`, `member-list`, `other-notable-events`, `chapter-whatsapp`,
 * `chapter-sponsors`) as part of removing their baked SectionHeader.
 * Users compose a separate `section-heading` Block above any of them
 * when they want a heading. Zod strip handles the silent field-removal
 * on parse; the v4 → v5 migration body just bumps `schemaVersion`.
 *
 * **v6 (P-037.21 D-128).** `events-list` Block's `options.headerStyle` +
 * `eyebrowText` + `state` all dropped — the Block's 3 baked-header arms
 * (eyebrow via SubSectionHeader, state via StateBar, none) were removed;
 * users compose a `sub-section-heading` Block above when they want a
 * heading (with optional state-flag via sub-section-heading's `state`
 * option). StateBar Component deleted. Zod strip + schemaVersion bump
 * only at the migration layer; field-removal happens at parse time.
 *
 * **v7 (P-037.22 D-130).** Block-inventory + per-block schema changes
 * from the eighth batched audit:
 * - `other-notable-events` Block dropped (filtered out of every blocks[]
 *   array — leftover instances would fail registry lookup at render time).
 * - `event-card-group.options.style` (`big-3 | signature | generic`) +
 *   `options.columns` (`2 | 3`) + `inputs.eyebrow` all dropped; the
 *   per-event `ctaLabel` narrows to the events-list enum
 *   (`Register Now | Add to Waitlist | Save the Date`). The new schemas
 *   are `z.object({})` (options) and the inputs flatten — Zod's default
 *   strip discards stale keys and out-of-enum `ctaLabel` values on parse.
 * - EyebrowLabel Component deleted (no remaining consumers).
 * - AuthorByline / DayChairItem / SponsorCard render-size updates and
 *   `src/lib/image-dimensions.ts` updates are render-layer (not
 *   schema-shape) changes; no migration step needed for them.
 *
 * **v8 (P-037.23 D-131).** Two block-shape changes from the ninth
 * batched audit:
 * - `chapter-sponsors` Block instances filtered out — old shape (name /
 *   tagline / description / websiteUrl + `options.layout`) doesn't map
 *   cleanly to the new fork-of-event-card-group shape (sponsorName /
 *   logoUrl? / caption? / sponsorWebsiteUrl?). Banks's "Remove current
 *   block" framing = drop existing instances + start fresh. Same
 *   explicit-filter playbook as v2 → v3 (`member-shoutout`), v3 → v4
 *   (`sponsors-needed-cta`), v6 → v7 (`other-notable-events`).
 * - `chapter-whatsapp.options.states` dropped + new `inputs.chapterWide-
 *   Groups` + `inputs.groupsByState` (per-Email-editable). Zod's default
 *   strip handles `options.states` removal on parse; the new inputs
 *   have `.default(...)` so persisted state without them gets the
 *   chapter-default seed at parse time — no explicit migration body
 *   needed for chapter-whatsapp.
 * - `event-card-group.events[].imageUrl` + `chapter-sponsors.sponsors[].
 *   logoUrl` switched to `.optional()` (P-037.23 D-131 fallback contract).
 *   Schema-shape change is "narrower → wider" so old persisted state
 *   with the field still validates; no migration needed.
 * - `footer.options` schema-shape unchanged from v6 (already `z.object({})`);
 *   the `ctx.tintColor` + logo-image swap are render-layer changes.
 *
 * **v9 (P-037.27 D-135).** Relax `Template.name` + `Email.name` from
 * `z.string().min(1)` → `z.string()` so staff can clear the inline-
 * editable title to empty + have the placeholder copy render at view
 * time only (`name || 'Untitled template'`). No data transformation:
 * old persisted records (all have name.length >= 1 via the
 * `createEmpty*` / `createEmailFromTemplate` factory seeds) still
 * validate under the relaxed envelope. Bump is convention-only (every
 * shape change has bumped since v2) — the migrations[8] entry is a
 * `makeBlocksMigration(9)` pass-through that stamps `schemaVersion: 9`.
 * The placeholder copy is render-time JSX fallback only — NEVER
 * persisted to storage (keeps the data layer clean of UX strings).
 *
 * **v10 (P-037.28 D-136).** Block-inventory expansion (drop 2 + add 7
 * net):
 * - `commentary` Block dropped (filtered out of every blocks[] array).
 *   Split into 4 atomic Blocks (headline / text / button / author) so
 *   staff can compose "letter"-style emails freely without commentary's
 *   hard-coded headline+body+byline order.
 * - `text-block` Block dropped (filtered out). Same atomic split — the
 *   new `text` + `button` Blocks cover everything text-block did. No
 *   longer needed.
 * - 9 new atomic Blocks added through the registry: `headline`, `text`,
 *   `button`, `author` (the 4 atomic-letter Blocks), `prospect-list`,
 *   `images`, `host-committee`, `big-3-hero-image`, `time-location-cost`.
 *   Registry-only — no schema-shape migration needed for additive
 *   Blocks (same precedent as `big3-signature-events` at D-132).
 * - NEW `ctx.ctaBgColor` field on RenderContext (renderEmail walks the
 *   first included Header → `STYLE_CTA_BG[style]`); the new `button`
 *   Block consumes it. Same pattern as the D-118 linkColor / D-125
 *   subheaderImageUrl / D-131 tintColor derivations.
 * - Tiptap config extended with BulletList + OrderedList — chapter
 *   "letter"-style bodies need list formatting. DOMPurify allow-list
 *   extends to `ul + ol + li` in lockstep.
 *
 * **v11 (P-037.29 D-137).** Three richText fields dropped from existing
 * instances: `day-chairs.inputs.intro` (optional), `chapter-whatsapp.
 * inputs.description` (required pre-v11), `member-list.inputs.body`
 * (optional). Zod-strip pattern handles the field-removal at parse
 * time; the migrations[10] entry just stamps `schemaVersion: 11`.
 *
 * **v12 (P-037.31 D-139).** Additive optional `defaultInputs?` on the
 * Template's per-Block-instance shape. Lets Template authors set
 * per-instance `inputs` defaults that pre-fill into Emails at
 * `createEmailFromTemplate` time (Tech Arch §2.5 merge order:
 * `BlockDefinition.defaultInputs` → Template-instance `defaultInputs`).
 * V1 surfaces the affordance for 3 Blocks only (`section-heading`,
 * `sub-section-heading`, `text`); other Blocks parse the field but the
 * Template editor doesn't render a Defaults section for them. Old
 * persisted Template state lacks the field — Zod's `.optional()`
 * accepts both missing-key and explicit-`undefined` shapes. The
 * migrations[11] entry just stamps `schemaVersion: 12`; no per-block
 * walk needed (same Zod-pass-through pattern as v4→v5 / v5→v6 / v8→v9
 * / v10→v11).
 *
 * **v13 (P-037.30 sixth post-Phase-Close §D).** `images` Block's
 * `columns` field migrates from `options` → `inputs` so chapter staff
 * can pick the layout per-Email (rather than locking it at the Template
 * scope). Per-record-kind migration: for each `images` Block instance,
 * if `options.columns` is present, the migration relocates it:
 * - **Template instance** → `defaultInputs.columns` so the staff-
 *   intended default flows into Emails created from this Template
 *   post-migration via the D-139 `mergeDefaultInputs` path.
 * - **Email instance** → `inputs.columns` so the existing Email
 *   retains its rendered layout.
 * Then `columns` is stripped from `options`. `images` Blocks without a
 * persisted columns value (e.g., test fixtures, defaults-only) pass
 * through unchanged. New `inputs.columns` field has `.default('3')` as
 * a belt-and-suspenders safety net for any persisted state that lacks
 * the field after migration. D-139 whitelist
 * (`BLOCKS_WITH_DEFAULTABLE_INPUTS` in `template-editor.tsx`) NOT
 * extended in this batch — surfacing a Template-editor Defaults UI for
 * `images.columns` is a separate UX expansion. The data path is there
 * if a future batch surfaces it.
 *
 * **v14 (P-047 / D-142).** Email gains `subjectLine: z.string()` (no
 * `.min(1)` — mirrors the D-135 `name` relaxation; empty allowed at
 * the schema layer, UI shows a placeholder hint, V2.2 Push button
 * gates on non-empty at P-048). Plus `previewText: z.string().optional()`
 * (preheader text; surfaces in the inbox-list preview row). The v13 →
 * v14 migration adds the fields to every persisted Email (existing
 * shape was field-less); Templates carry no subject/preview info, so
 * the migration just stamps `schemaVersion: 14` on Templates and walks
 * the Emails to seed the new fields. `.default('')` on the Zod schema
 * + factory + migration all converge on the same canonical "empty +
 * present" shape, which `.min(1)` would have rejected at parse time —
 * the no-min relaxation is intentional, not a Zod oversight.
 */
export const CURRENT_SCHEMA_VERSION = 14

export type Template = z.infer<typeof TemplateSchema>
export type Email = z.infer<typeof EmailSchema>
export type AppState = z.infer<typeof AppStateSchema>
export type EmailBlockInstance = z.infer<typeof EmailSchema>['blocks'][number]
export type TemplateBlockInstance = z.infer<typeof TemplateSchema>['blocks'][number]
